// Auth-related localStorage keys, extracted to a leaf module so both
// `useAuthStore` (which reads them at module-load time to compute initial
// state) and `authService` (which reads/writes them during the auth flow)
// can import them without creating a circular dependency.
//
// Naming convention in core: bare `hp_` prefix, no app slug.
//
// The three sibling starter apps (admin/www/chef) carry their own copies of
// these constants with a per-app slug (`hp_a_*`, `hp_w_*`, `hp_c_*`) so each
// origin's localStorage is self-descriptive in DevTools. Core stays bare
// because it predates the multi-app split and existing live keys here
// (`hp-location`, `hp_device`, etc.) follow the no-slug pattern.
//
// The JWT/EMAIL/SECRET trio is only written in localStorage session mode
// (`VITE_SESSION_MODE=localStorage`). In cookie mode (the default for
// staging/prod) the JWT lives in an HttpOnly cookie and these keys are
// never set — kept here so the localStorage code path still compiles + works
// for dev configs that opt out of cookie mode.

export const JWT_STORAGE_KEY = 'hp_jwt';
export const EMAIL_STORAGE_KEY = 'hp_email';
export const SECRET_STORAGE_KEY = 'hp_secret';

// Holds a JSON blob `{ isRegistered: boolean }`.
// Written at the end of every successful whoami, cleared on logout. Used by
// `useAuthStore` to hydrate initial gate state synchronously at boot so the
// RegistrationGate can paint immediately without waiting on the whoami
// round-trip.
export const GATES_STORAGE_KEY = 'hp_gates';

// sessionStorage timestamp (Date.now() string) stamped by ShareRedirectPage on
// mount. `logout()` reads this and SKIPS its `window.location.assign('/')`
// when the stamp is within the grace window — covers the boot-time race where
// a stale-cookie visitor's share resolve and the auth chain's
// whoami→refresh→logout fire concurrently. Without this guard, the auth
// chain's hard-redirect blows the visitor out to home mid-share-arrival.
// sessionStorage (not localStorage) because the flag is per-tab and naturally
// clears on tab close.
export const SHARE_ARRIVAL_AT_KEY = 'hp_share_arrival_at';
// 30s comfortably covers the bootstrap race (share resolve ~200ms; auth chain
// whoami→refresh→logout ~600ms). Later legitimate logouts (token expiry
// mid-session, manual sign-out, profile switch) all happen far beyond this
// window and still hard-redirect normally.
export const SHARE_ARRIVAL_GRACE_MS = 30_000;

/**
 * Returns true when the current tab is inside the share-arrival grace window.
 * Both `logout()` and the apiService `triggerAuthFailureLogout` catch-fallback
 * (which fires if `logout()` itself throws) call this before doing
 * `window.location.assign('/')` — so a stale-cookie share-link visitor lands
 * on the listing they clicked through to, not home.
 *
 * Defensive against every failure mode I could think of: missing flag,
 * corrupted timestamp (`parseInt` → NaN comparison fails), clock skew (negative
 * elapsed), sessionStorage unavailable (private mode / quota). Any failure
 * returns false so the default redirect runs.
 */
export function isWithinShareArrivalGrace(): boolean {
  try {
    const stampedAt = sessionStorage.getItem(SHARE_ARRIVAL_AT_KEY);
    if (!stampedAt) return false;
    const elapsed = Date.now() - parseInt(stampedAt, 10);
    return elapsed >= 0 && elapsed < SHARE_ARRIVAL_GRACE_MS;
  } catch {
    return false;
  }
}
